import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { UserSettingsContext } from './helpers';
import { useQuery } from 'react-query';
import { QueryKey } from '@/lib/query-client';
import { getAllDatasources } from '@/lib/services/datasource.service';
import { DatasourceModel } from '@/lib/models/datasource.model';
import useApp from '@/hooks/use-app.hook';
import { getAvailableAgents, getDefaultOrgAgent } from '@/lib/services/shortcut.service';
import { getPrivateModelConfig } from '@/lib/services/model.service';
import {
  getUserSettings,
  getUserSettingsLocal,
  removeUserSettingsLocal,
  updateUserSettings,
} from '@/lib/services/user-setting.service';
import { OrgBrandingModel } from '@/lib/models/org-branding.model';
import { AgentModel } from '@/lib/models/agent.model';
import { UserSettingsModel } from '@/lib/models/user-settings/user-settings.model';
import useAppTheme from '@/hooks/use-app-theme.hooks';
import { has } from 'lodash';
import { LoadState } from '@/lib/helpers';
import { HomeModel } from '@/lib/models/user-settings/home.model';
import { PreferencesModel } from '@/lib/models/user-settings/preferences.model';
import { ThreadHistoryModel } from '@/lib/models/user-settings/thread-history.model';
import { TileModel } from '@/lib/models/user-settings/tile.model';
import useAppData from '@/hooks/use-app-data.hook';
import { PublicServiceMeta } from '@/lib/models/public-service';
import useToast from '@/hooks/use-toast.hook';

export const UserSettingsProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const [settings, setUserSettings] = useState(new UserSettingsModel());
  const [loadState, setLoadState] = useState<LoadState>('unloaded');

  const { signedIn } = useApp();
  const { setColorMode: setAppThemeColorMode, colorMode: appThemeColorMode } = useAppTheme();
  const { errorToast } = useToast();

  const { data: branding } = useAppData<OrgBrandingModel>('org-branding', { enabled: signedIn });

  // backwards compat with the old method of saving settings to local storage
  const { data: localSettings } = useQuery(
    [QueryKey.UserSettingsLocal],
    () => getUserSettingsLocal(),
    {
      enabled: signedIn,
    }
  );

  const { data: rawSettings } = useQuery([QueryKey.UserSettings], () => getUserSettings(), {
    enabled: signedIn,
  });

  const { data: loadedDatasources } = useQuery<DatasourceModel[]>(
    [QueryKey.DatasourceList],
    async () => getAllDatasources(),
    {
      enabled: signedIn,
    }
  );

  const { data: loadedAgents } = useQuery([QueryKey.AgentsList], async () => getAvailableAgents(), {
    enabled: signedIn,
  });

  const { data: loadedPrivateModels } = useQuery(
    [QueryKey.PrivateModelsList],
    async () => getPrivateModelConfig(),
    {
      enabled: signedIn,
    }
  );

  const { data: publicServices = [] } = useAppData<PublicServiceMeta[]>('public-service-catalog', {
    enabled: signedIn,
  });

  const updateUserSettingsWithToast = useCallback(
    (settings: UserSettingsModel) => {
      updateUserSettings(settings).then(({ updated }) => {
        if (!updated) {
          errorToast('Saving user settings failed. Please try again in a few moments.');
        }
      });
    },
    [errorToast]
  );

  const handleSetAgentId = useCallback(
    (agentId: string) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.thread.agentId = agentId;
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleSetPrivateModelId = useCallback(
    (privateModelId: string) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.thread.privateModelId = privateModelId;
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleTogglePublicSearch = useCallback(
    (enabled: boolean) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.thread.togglePublicSearch(enabled);
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleToggleDatasource = useCallback(
    (datasourceId: string | string[], enabled: boolean) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.thread.toggleDatasource(datasourceId, enabled);
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handlePatchPreferences = useCallback(
    (data: Partial<PreferencesModel>) => {
      if (has(data, 'colorMode')) {
        setAppThemeColorMode(data.colorMode!);
      }
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.preferences = { ...updated.portal.preferences, ...data };
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [setAppThemeColorMode, updateUserSettingsWithToast]
  );

  const handleToggleFavorite = useCallback(
    (tile: TileModel, addFavorite = true) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        if (addFavorite) {
          updated.portal.home.addFavorite(tile);
        } else {
          updated.portal.home.removeFavorite(tile.tileId);
        }
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleMoveFavorite = useCallback(
    (tileId: string, targetTileId: string) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.home.moveFavorite(tileId, targetTileId);
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleToggleRecent = useCallback(
    (tile: TileModel, addRecent = true) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        if (addRecent) {
          updated.portal.home.addRecent(tile);
        } else {
          updated.portal.home.removeRecent(tile.tileId);
        }
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const handleUpdateThreadHistory = useCallback(
    (updater: (prev: ThreadHistoryModel[]) => ThreadHistoryModel[]) => {
      setUserSettings((old) => {
        const updated = new UserSettingsModel(old._props);
        updated.portal.threadHistory = updater(updated.portal.threadHistory);
        updateUserSettingsWithToast(updated);
        return updated;
      });
    },
    [updateUserSettingsWithToast]
  );

  const brandedAgents = useMemo(() => {
    if (!branding || !loadedAgents) {
      return;
    }
    return [getDefaultOrgAgent(branding), ...loadedAgents];
  }, [branding, loadedAgents]);

  const defaultAgent = useMemo(
    () => brandedAgents?.find(({ isDefaultAgent }) => isDefaultAgent),
    [brandedAgents]
  );

  /*
   * Load thread user settings once all dependent resources are loaded
   */
  useEffect(() => {
    if (
      !Array.isArray(loadedDatasources) ||
      !loadedPrivateModels ||
      !brandedAgents ||
      !rawSettings?.portal ||
      loadState == 'loaded'
    ) {
      return;
    }

    setLoadState('loaded');

    setUserSettings((old) => {
      const updated = new UserSettingsModel({ ...old._props, id: rawSettings.id });
      let rawThreadSettings = rawSettings.portal.thread;

      // backwards compat, see note above about localSettings
      if (localSettings?.thread) {
        rawThreadSettings = localSettings.thread;
        removeUserSettingsLocal();
      }

      // thread settings validated against latest version of available resources
      updated.portal.thread.loadAndValidate(
        rawThreadSettings,
        brandedAgents,
        loadedPrivateModels,
        loadedDatasources
      );

      // preferences
      const { preferences } = rawSettings.portal;
      updated.portal.preferences = {
        ...preferences,
        colorMode: appThemeColorMode,
      };

      // home tiles
      const { home } = rawSettings.portal;
      updated.portal.home = new HomeModel({
        ...home,
      });

      // thread history (already parsed by UserSettingsModel constructor)
      updated.portal.threadHistory = Array.isArray(rawSettings.portal.threadHistory)
        ? rawSettings.portal.threadHistory
        : [];

      // sync with remote
      updateUserSettings(updated);

      return updated;
    });
  }, [
    loadedDatasources,
    loadedPrivateModels,
    brandedAgents,
    rawSettings,
    appThemeColorMode,
    localSettings,
    loadState,
  ]);

  return (
    <UserSettingsContext.Provider
      value={{
        threadSettings: settings.portal.thread,
        datasources: loadedDatasources || [],
        publicSearchAvailable: !!loadedDatasources?.some(
          ({ isPublicInternet }) => isPublicInternet
        ),
        togglePublicSearch: handleTogglePublicSearch,
        toggleDatasource: handleToggleDatasource,
        agents: brandedAgents || [],
        setAgentId: handleSetAgentId,
        defaultAgent: defaultAgent || new AgentModel(),
        privateModelSettings: loadedPrivateModels || {
          defaultPrivateModelId: '',
          availablePrivateModels: [],
        },
        setPrivateModelId: handleSetPrivateModelId,
        publicServices,
        preferences: settings.portal.preferences,
        patchPreferences: handlePatchPreferences,
        home: settings.portal.home,
        toggleFavorite: handleToggleFavorite,
        moveFavorite: handleMoveFavorite,
        toggleRecent: handleToggleRecent,
        threadHistory: settings.portal.threadHistory,
        updateThreadHistory: handleUpdateThreadHistory,
        isLoading: loadState !== 'loaded',
      }}
    >
      {children}
    </UserSettingsContext.Provider>
  );
};
